/**
 * This file creates a fake login. Every dashboard sits behind ProtectedRoute,
 * which redirects to /authorization unless AuthContext reports the user as
 * authenticated, and AuthContext decides that by reading three localStorage keys:
 * access_token, user, and last_activity. So this file defines a fake user and
 * writes those three keys before React mounts. The app then renders the real
 * dashboards as if someone had logged in. ProtectedRoute is never modified.
 *
 * The token is a dummy string the backend would reject, and in demo mode nothing
 * reaches the backend anyway, so it unlocks rendering only, never data. The fake
 * user also carries the membership tier, which decides whether premium surfaces
 * such as Fair Price appear, plus the feature flags the demo should show.
 */

import { ACCESS_LEVELS, type FeatureFlagPublic, type User } from '@rti/shared';

export interface DemoPersona {
  user: User;
  /** Flags served to the intercepted getFeatureFlags() call. */
  featureFlags: FeatureFlagPublic[];
}

/** Flags a polished demo should show. Keys must exist in the FeatureFlag table. */
const DEFAULT_DEMO_FLAGS = ['left_nav', 'landing_page', 'policy_dashboard'];

/**
 * PRIME (3) clears the COMPOUND (2) gate, so premium surfaces such as Fair Price
 * render. is_admin stays false: admin routes must not open in a public demo.
 */
export const DEFAULT_DEMO_PERSONA: DemoPersona = {
  user: {
    user_id: 999000,
    email: 'demo@resinsmart.ai',
    first_name: 'Demo',
    last_name: 'User',
    company_name: 'ResinSmart Demo Co.',
    is_active: true,
    is_admin: false,
    unique_passport: 'demo-passport',
    created_at: new Date(0).toISOString(),
    effective_membership_id: ACCESS_LEVELS.PRIME,
    company_id: 999000,
    company_role: 'member',
    on_free_trial: false,
    pending_company_approval: false,
    needs_password_update: false,
  },
  featureFlags: DEFAULT_DEMO_FLAGS.map((feature_key) => ({ feature_key })),
};

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';
const LAST_ACTIVITY_KEY = 'last_activity';
const IMPERSONATED_USER_KEY = 'impersonated_user';

/** Marker so a demo session is distinguishable from a real one, and cleanable. */
export const DEMO_TOKEN = 'demo-mode-not-a-real-token';

export function seedFakeAuth(persona: DemoPersona = DEFAULT_DEMO_PERSONA): void {
  localStorage.setItem(TOKEN_KEY, DEMO_TOKEN);
  localStorage.setItem(USER_KEY, JSON.stringify(persona.user));
  // Required: AuthContext.isTokenExpired() clears the seed without a fresh stamp.
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  // A real session's impersonation state must not bleed into the demo.
  localStorage.removeItem(IMPERSONATED_USER_KEY);
}

export function clearFakeAuth(): void {
  if (localStorage.getItem(TOKEN_KEY) !== DEMO_TOKEN) return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}
